import * as React from 'react'
import { useCallback, useState } from 'react'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/MailOutline'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { EntityDetail } from '@user/store/map/typings'
import { useData } from '../hooks/useData'
import { ContactsDialog } from '@user/components/Contacts/ContactsDialog'

type Props = {
  entityDetails: EntityDetail | null
  serviceLocation: LocationDTO
  variant: string
  defaultOpen?: boolean
  canUpdate?: boolean
}

export function PrimaryContactInfo(props: Props) {
  const { entityDetails, serviceLocation, variant, canUpdate } = props
  const isPendingLead = variant === 'pendinglead' || variant === 'draftlead'

  const [showContacts, setShowContacts] = useState(false)

  const { formattedPhone, email } = useData({
    entityDetails,
    serviceLocation,
    variant,
    defaultOpen: props.defaultOpen,
  })

  const entityType = React.useMemo(() => {
    switch (variant.toLowerCase()) {
      case 'lead':
        return ENTITY_TYPES.Lead
      case 'opportunity':
        return ENTITY_TYPES.Opportunity
      case 'draftlead':
        return ENTITY_TYPES.DraftLead
      default:
        return ENTITY_TYPES.Lead
    }
  }, [variant])

  const onPhoneNumberClick = useCallback(() => {
    if (formattedPhone) {
      window.open(`tel:${formattedPhone}`, '_blank')
    }
  }, [formattedPhone])

  const onEmailClick = useCallback(() => {
    if (email) {
      window.open(`mailto:${email}`, '_blank')
    }
  }, [email])

  const handleManageContacts = useCallback(() => {
    setShowContacts(true)
  }, [])

  const handleCloseContacts = useCallback(() => {
    setShowContacts(false)
  }, [])

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          Primary Contact Information
        </Typography>

        {formattedPhone ? (
          <Button
            onClick={onPhoneNumberClick}
            variant="text"
            startIcon={<PhoneIcon sx={{ fontSize: 24, color: '#1565C0' }} />}
            sx={{ color: '#1565C0', width: 'fit-content' }}
          >
            {formattedPhone}
          </Button>
        ) : null}
        {email ? (
          <Button
            onClick={onEmailClick}
            variant="text"
            startIcon={<EmailIcon sx={{ fontSize: 24, color: '#1565C0' }} />}
            sx={{ color: '#1565C0', width: 'fit-content' }}
          >
            {email}
          </Button>
        ) : null}

        {!isPendingLead && (
          <Button
            variant="text"
            onClick={handleManageContacts}
            sx={{
              color: '#1565C0',
              width: 'fit-content',
            }}
            endIcon={<ChevronRightIcon />}
          >
            Manage Contacts
          </Button>
        )}
      </Paper>
      {!isPendingLead && (
        <ContactsDialog
          canUpdateEntity={canUpdate ?? false}
          entityId={entityDetails?.id ?? 0}
          entityType={entityType}
          open={showContacts}
          onClose={handleCloseContacts}
        />
      )}
    </>
  )
}
